import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { portalApi, portalError } from "./portalApi";
import { Btn, Input, Label } from "../components/UI";
import { Camera, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function PortalProfile() {
  const { me, refreshMe } = useOutletContext();
  const c = me.client;
  const [form, setForm] = useState({ preferred_name: c.preferred_name || "", phone: c.phone || "", email: c.email || "",
    address: c.address || "", city: c.city || "", state: c.state || "", zip: c.zip || "", photo: c.photo || "" });
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 350_000) return toast.error("Photo must be under 350 KB");
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setBusy(true);
    try {
      await portalApi.put("/portal/profile", form);
      toast.success("Profile updated");
      refreshMe();
    } catch (err) { toast.error(portalError(err)); }
    setBusy(false);
  };

  const changePw = async (e) => {
    e.preventDefault();
    try {
      await portalApi.post("/portal/auth/change-password", { new_password: pw });
      toast.success("Password changed"); setPw("");
    } catch (err) { toast.error(portalError(err)); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="portal-profile-page">
      <div>
        <h1 className="font-heading text-xl font-bold text-[#3E2723]">My Profile</h1>
        <p className="text-sm text-[#7B6157]">Update your contact details & photo. Clinical information can only be changed by your care team.</p>
      </div>

      <div className="rounded-2xl bg-white border border-[#EFEBE0] p-5 space-y-4">
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 rounded-full bg-[#8D6E63] flex items-center justify-center text-white font-heading text-2xl font-bold overflow-hidden">
            {form.photo ? <img src={form.photo} alt="" className="h-20 w-20 object-cover" data-testid="portal-photo-preview" /> : <>{c.first_name?.[0]}{c.last_name?.[0]}</>}
          </div>
          <div>
            <input id="portal-photo-file" data-testid="portal-photo-input" type="file" accept="image/*" onChange={onPhoto} className="hidden" />
            <label htmlFor="portal-photo-file" className="inline-flex items-center gap-1.5 cursor-pointer rounded-full bg-[#4E342E] px-4 py-2 text-xs font-bold text-white hover:bg-[#5D4037]"><Camera size={13} /> Change photo</label>
            {form.photo && <button data-testid="portal-photo-remove" onClick={() => setForm((s) => ({ ...s, photo: "" }))} className="block mt-2 text-xs font-semibold text-[#C62828] hover:underline">Remove</button>}
          </div>
          <div className="ml-auto text-right">
            <div className="font-bold text-[#3E2723]">{c.first_name} {c.last_name}</div>
            <div className="text-xs text-[#7B6157]">HRN {c.health_record_number || "-"} · DOB {c.dob || "-"}</div>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>Preferred name</Label><Input data-testid="portal-profile-preferred" value={form.preferred_name} onChange={(e) => setForm({ ...form, preferred_name: e.target.value })} /></div>
          <div><Label>Phone</Label><Input data-testid="portal-profile-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div><Label>Email</Label><Input data-testid="portal-profile-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Street address</Label><Input data-testid="portal-profile-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
            <div><Label>ZIP</Label><Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} /></div>
          </div>
        </div>
        <div className="flex justify-end"><Btn data-testid="portal-profile-save" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save Changes"}</Btn></div>
      </div>

      <form onSubmit={changePw} className="rounded-2xl bg-white border border-[#EFEBE0] p-5">
        <h3 className="flex items-center gap-1.5 font-heading text-sm font-bold text-[#3E2723] mb-3"><KeyRound size={14} className="text-[#7B6157]" /> Change Password</h3>
        <div className="flex gap-2">
          <Input data-testid="portal-changepw-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 8 characters)" minLength={8} required className="flex-1 max-w-xs" />
          <Btn data-testid="portal-changepw-submit" type="submit">Update</Btn>
        </div>
      </form>
    </div>
  );
}
